import React, { useEffect, useId, useRef, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import PauseRounded from '@mui/icons-material/PauseRounded';
import PlayArrowRounded from '@mui/icons-material/PlayArrowRounded';
import { largeSourceImage } from './trackHelper';
import PreviewFallbackImage from './PreviewFallbackImage';
import PreviewArtworkBackground from './PreviewArtworkBackground';
import PreviewArtworkWaveform from './PreviewArtworkWaveform';
import { getPreviewTopic } from '../utils/previewTopic';
import { displaySpeakerDescription, isUnidentifiedSpeaker, UNKNOWN_SPEAKER_IMAGE_URL } from './speakerHelper';
import FollowOutline from './FollowOutline';
import useFollowedEntities from './useFollowedEntities';
import './PreviewImage.css';

const PreviewImage = ({
  preview,
  displayTitle,
  onImageClick,
  imageDisabled = false,
  onSourceClick,
  onTopicClick,
  topicActionLabel,
  topicLabel,
  imageActionLabel = `Play ${preview.title || 'preview'}`,
  imageBorderRadius,
  highlightLayout = false,
  titleClassName = '',
  renderCaption,
  isPreparingAudio = false,
  onPlaybackClick,
  playing = false,
  playbackButtonRef,
  showPlaybackControl = true,
  fadePlaybackControl = false,
  playbackControlAvailable = true,
}) => {
  const isFollowed = useFollowedEntities();
  const sourceFollowed = isFollowed('show', { source: preview.source });
  const previewImageURL = preview.imageURL?.trim() || '';
  const speakerImageURL = preview.person?.imageURL?.trim() || '';
  const sourceImageURL = preview.source?.imageURL?.trim() || '';
  const largeSourceImageURL = largeSourceImage(sourceImageURL);
  const sourceName = preview.source?.name || 'Podcast';
  const topic = typeof topicLabel === 'string' ? topicLabel : getPreviewTopic(preview);
  const titleLabelId = useId();
  const speakerDetailsId = useId();
  const audioStatusId = useId();
  const [failedImageURLs, setFailedImageURLs] = useState([]);
  const [audioLoadingSource, setAudioLoadingSource] = useState(null);
  const [playbackFocused, setPlaybackFocused] = useState(false);
  const playbackPointerFocus = useRef(false);
  const audioSourceKey = `${preview.id || ''}:${preview.mp3URL || ''}`;

  useEffect(() => {
    setAudioLoadingSource(null);
    if (!isPreparingAudio) return undefined;
    const timer = setTimeout(() => setAudioLoadingSource(audioSourceKey), 500);
    return () => clearTimeout(timer);
  }, [isPreparingAudio, audioSourceKey]);
  const showAudioLoading = isPreparingAudio && (Boolean(onPlaybackClick) || audioLoadingSource === audioSourceKey);
  const playbackVisible = playbackControlAvailable && (showPlaybackControl || playbackFocused);

  useEffect(() => {
    setFailedImageURLs([]);
  }, [previewImageURL, speakerImageURL, sourceImageURL]);

  const isImageAvailable = (url) => Boolean(url) && !failedImageURLs.includes(url);
  const handleImageError = (url) => {
    setFailedImageURLs(previous => previous.includes(url) ? previous : [...previous, url]);
  };

  const hasPreviewImage = isImageAvailable(previewImageURL);
  const hasSpeakerImage = !isUnidentifiedSpeaker(preview.person)
    && speakerImageURL !== UNKNOWN_SPEAKER_IMAGE_URL
    && isImageAvailable(speakerImageURL);
  const sourceFallbackImage = [largeSourceImageURL, sourceImageURL].find(isImageAvailable);
  const fallbackImage = hasSpeakerImage ? speakerImageURL : sourceFallbackImage;
  const sourceBadgeImage = [sourceImageURL, largeSourceImageURL].find(isImageAvailable);
  const title = (typeof displayTitle === 'string' ? displayTitle.trim() : '') ||
    (typeof preview.title === 'string' ? preview.title.trim() : '') || 'Highlight';
  const speakerDescription = highlightLayout ? '' : displaySpeakerDescription(preview.person).trim();
  const showsTitleArtwork = highlightLayout && !hasPreviewImage;
  const speakerDescriptionId = !hasPreviewImage && !highlightLayout && speakerDescription ? speakerDetailsId : undefined;
  const artworkDescriptionId = showsTitleArtwork ? titleLabelId : speakerDescriptionId;

  const sourceButton = (
    <button
      type='button'
      onClick={() => onSourceClick?.(preview.source?.id)}
      aria-label={`Open podcast: ${sourceName}${sourceFollowed ? ", followed" : ""}`}
      className='previewArtworkSource'
      disabled={!preview.source?.id}
      style={{
        position: 'relative',
        zIndex: 3,
        width: '49px',
        height: '49px',
        borderRadius: 0,
        border: 'none',
        background: 'transparent',
        color: 'var(--app-text-secondary)',
        padding: 0,
        overflow: 'visible',
        cursor: 'pointer',
      }}
    >
      <FollowOutline followed={sourceFollowed}>
      <span style={{ width: 44, height: 44, borderRadius: 0,
        display: 'grid', placeItems: 'center', overflow: 'hidden', background: 'var(--app-surface-tertiary)' }}>
      {sourceBadgeImage ? (
        <img
          src={sourceBadgeImage}
          className='sourceImage'
          style={{ width: '100%', height: '100%', minWidth: 0, minHeight: 0, objectFit: 'contain', display: 'block' }}
          alt={sourceName}
          loading='lazy'
          onError={() => handleImageError(sourceBadgeImage)}
        />
      ) : <span aria-hidden='true'>{sourceName.charAt(0).toUpperCase()}</span>}
      </span>
      </FollowOutline>
    </button>
  );
  return (
    <>
      <div className={`previewImageFrame${highlightLayout ? ' previewInfoImageFrame' : ''}`}
        data-artwork={hasPreviewImage ? 'image' : 'fallback'}
        data-has-topic={Boolean(topic)}
        data-has-playback={Boolean(onPlaybackClick)}
        style={{ '--preview-image-radius': imageBorderRadius || '0px' }}>
        <button
          type='button'
          onClick={onImageClick}
          disabled={imageDisabled}
          aria-label={imageActionLabel}
          aria-describedby={[artworkDescriptionId,
            showAudioLoading && !onPlaybackClick ? audioStatusId : undefined].filter(Boolean).join(' ') || undefined}
          style={{
            width: '100%',
            position: 'relative',
            display: 'block',
            border: 'none',
            background: hasPreviewImage ? 'var(--app-surface-tertiary)' : 'transparent',
            padding: 0,
            cursor: 'pointer',
            aspectRatio: hasPreviewImage ? '16/9' : undefined,
            overflow: 'hidden',
            borderRadius: imageBorderRadius,
          }}
        >
          {hasPreviewImage ? (
            <img
              src={previewImageURL}
              className='previewCardPreviewImage'
              alt='Preview'
              loading='lazy'
              onError={() => handleImageError(previewImageURL)}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : showsTitleArtwork ? (
            <PreviewArtworkBackground
              className='previewInfoFallbackArtwork'
              backgroundImage={sourceFallbackImage}
              onBackgroundError={() => handleImageError(sourceFallbackImage)}>
              <PreviewArtworkWaveform />
              <span id={titleLabelId} className={['previewInfoTitle', 'previewInfoArtworkTitle', titleClassName].filter(Boolean).join(' ')}>{title}</span>
            </PreviewArtworkBackground>
          ) : (
            <PreviewFallbackImage
              preview={preview}
              speakerDescription={speakerDescription}
              speakerDetailsId={speakerDescriptionId}
              foregroundImage={fallbackImage}
              backgroundImage={sourceFallbackImage}
              circular={hasSpeakerImage}
              followed={hasSpeakerImage ? isFollowed('speaker', preview.person) : sourceFollowed}
              alt={hasSpeakerImage ? preview.person.name : sourceName}
              onError={() => handleImageError(fallbackImage)}
              onBackgroundError={() => handleImageError(sourceFallbackImage)}
            />
          )}
        </button>

        {!onPlaybackClick && <span id={audioStatusId} className='previewArtworkAudioLoading'
          role='status' aria-label={showAudioLoading ? 'Preparing audio' : undefined}>
          {showAudioLoading && <>
            <CircularProgress size={20} thickness={4} color='inherit' aria-hidden='true' />
            <span className='visuallyHidden'>Preparing audio</span>
          </>}
        </span>}

        <div className='previewArtworkControls'>
          {onPlaybackClick && <button type='button' ref={playbackButtonRef}
            className='previewArtworkPlaybackButton' onClick={onPlaybackClick}
            data-visible={playbackVisible}
            data-fade={fadePlaybackControl}
            data-available={playbackControlAvailable}
            tabIndex={playbackControlAvailable ? 0 : -1}
            aria-hidden={!playbackControlAvailable || undefined}
            onPointerDown={() => {
              playbackPointerFocus.current = true;
              setPlaybackFocused(false);
            }}
            onFocus={() => setPlaybackFocused(!playbackPointerFocus.current)}
            onKeyDown={() => setPlaybackFocused(true)}
            onBlur={() => {
              playbackPointerFocus.current = false;
              setPlaybackFocused(false);
            }}
            aria-label={isPreparingAudio || playing ? 'Pause highlight' : 'Play highlight'}
            aria-describedby={showAudioLoading ? audioStatusId : undefined}>
            {showAudioLoading ? <CircularProgress size={22} thickness={4} color='inherit' aria-hidden='true' />
              : playing ? <PauseRounded aria-hidden='true' /> : <PlayArrowRounded aria-hidden='true' />}
            <span id={audioStatusId} className='visuallyHidden' role='status'
              aria-label={showAudioLoading ? 'Preparing audio' : undefined}>
              {showAudioLoading ? 'Preparing audio' : ''}
            </span>
          </button>}
          {topic && (onTopicClick ?
            <button type='button' className='previewArtworkTopic' onClick={onTopicClick}
              aria-label={topicActionLabel || `Play preview and more on ${getPreviewTopic(preview, { uppercase: false })}`}>
              <span className='previewArtworkTopicText'>{topic}</span>
            </button> :
            <span className='previewArtworkTopic'><span className='previewArtworkTopicText'>{topic}</span></span>)}
          {sourceButton}
        </div>
      </div>
      {renderCaption?.(hasPreviewImage)}
    </>
  );
};

export default PreviewImage;
